import router from "@/router";
import store from "@/store";
import moment from "moment";
import { Message, MessageBox } from "element-ui"; //消息提示框

/**
 * 公共确认提示框
 * 公共工具函数库，提供：
 * - `ConfirmMessageBox()` - 确认提示框
 * - `CheckIsLogin()` - 检查登录状态
 * - 日期格式化函数
 * - 文件处理函数
 * - 时间处理函数
 * @param {*} config
 * @returns
 */
export function ConfirmMessageBox(config) {
  let _config = {
    title: config.title || "提示",
    content: config.content || "",
  };
  return new Promise(function (resolve, reject) {
    MessageBox.confirm(_config.content, _config.title, {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "warning",
    })
      .then(() => {
        resolve(true);
      })
      .catch(() => {
        resolve(false);
      });
  });
}

/**
 * 把相对路径转换成完整的可访问地址
 * 支持传入单个字符串或字符串数组
 * @param {*} value 路径或路径数组
 * @returns
 */
export function ReplaceImageHttp(value) {
  const toFullUrl = (url) => {
    if (url === null || url === undefined) return "";
    const u = String(url).trim();
    if (!u) return "";
    if (/^https?:\/\//i.test(u)) return u;
    return process.env.VUE_APP_BASE_API + (u.startsWith("/") ? u : "/" + u);
  };
  if (Array.isArray(value)) {
    return value.map(toFullUrl).filter((x) => x);
  }
  return toFullUrl(value);
}

/**
 * 获取路径中的文件名称
 */
export function GetFileNameByPath(path) {
  if (path === null || path === undefined || path === "") return "";
  const parts = String(path).split(/[\\/]/).filter((x) => x);
  return parts.length ? parts[parts.length - 1] : "";
}
/**
 * 获取路径中的文件格式
 */
export function GetFileTypeByPath(path) {
  const name = GetFileNameByPath(path);
  const idx = name.lastIndexOf(".");
  return idx === -1 ? "" : name.slice(idx + 1).toLowerCase();
}
/**
 * 切割字段返回一个集合
 * @param {*} value 需要切割字段
 * @param {*} cutting 切割的符号
 */
export function ConvertArray(value = "", cutting = ",") {
  if (value === null || value === undefined || value === "") return [];
  return String(value).split(cutting).filter((x) => x);
}
/**
 * 根据路径获取文件的详细信息
 */
export function FullConvertUrlArray(value = "", cutting = ",") {
  return ConvertArray(value, cutting)
    .map((x) => ReplaceImageHttp(x))
    .filter((x) => x);
}

/**
 * 把各类时间输入解析成 moment 对象,解析失败返回 null
 * 兼容 "2026-04-02 16:42:05"、ISO 格式、时间戳、Date 对象
 */
function ToMoment(val) {
  if (val === null || val === undefined || val === "") return null;
  if (moment.isMoment(val)) return val.clone();
  if (val instanceof Date) return isNaN(val.getTime()) ? null : moment(val);
  if (typeof val === "number") return moment(val);
  const s = String(val).trim();
  if (!s) return null;
  // "2026-04-02 16:42:05" 这种空格分隔的格式部分浏览器无法解析,统一替换成 ISO 格式
  const m = moment(s.replace(" ", "T"), [moment.ISO_8601, "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD"]);
  return m.isValid() ? m : null;
}

/**
 * 得到完整的时间格式 年-月-日 时分秒
 */
export function YMDHMSFormat(val) {
  const m = ToMoment(val);
  return m ? m.format("YYYY-MM-DD HH:mm:ss") : "";
}
/**
 * 得到完整的时间格式 年-月-日
 */
export function YMDFormat(val) {
  const m = ToMoment(val);
  return m ? m.format("YYYY-MM-DD") : "";
}

/**
 * 得到时分秒格式
 */
export function HMSFormat(val) {
  const m = ToMoment(val);
  return m ? m.format("HH:mm:ss") : "";
}
/**
 * 获取相对时间描述
 * @param {Date|string|number} date 要比较的时间
 * @returns {string} 返回相对时间描述
 */
export function GetRelativeTimeDesc(date) {
  const m = ToMoment(date);
  if (!m) return "";
  const diffSeconds = moment().diff(m, "seconds");
  if (diffSeconds < 60) return "刚刚";
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}分钟前`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}小时前`;
  if (diffSeconds < 86400 * 30) return `${Math.floor(diffSeconds / 86400)}天前`;
  return m.format("YYYY-MM-DD");
}

/**
 * 读取媒体文件时长的通用实现
 * @param {*} url 媒体地址
 * @param {*} type "video" 或 "audio"
 * @returns Promise<number> 时长(秒),失败返回 0
 */
function GetMediaDuration(url, type) {
  return new Promise((resolve) => {
    const fullUrl = ReplaceImageHttp(url);
    if (!fullUrl) {
      resolve(0);
      return;
    }
    const el = document.createElement(type);
    el.preload = "metadata";
    el.src = fullUrl;
    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      el.onloadedmetadata = null;
      el.onerror = null;
      el.src = "";
      resolve(value || 0);
    };
    el.onloadedmetadata = () => done(el.duration);
    el.onerror = () => done(0);
    // 防止媒体一直加载不出来导致永久挂起
    setTimeout(() => done(0), 10000);
  });
}

/**
 * 根据视频的路径获取视频的时长(秒)
 */
export function GetVideoDuration(url) {
  return GetMediaDuration(url, "video");
}
/**
 * 根据路径获取音频的长度(秒)
 */
export function GetAudioDuration(url) {
  return GetMediaDuration(url, "audio");
}

/**
 * 检查是否登录状态
 * @returns
 */
export function CheckIsLogin() {
  if (!store.getters.Token) {
    setTimeout(() => {
      router.push("/login");
    }, 500);
    Message({
      showClose: true,
      message: "请先登录后,再操作",
      type: "error",
    });
    return false;
  }
  return true;
}
export default {
  CheckIsLogin,
  ConfirmMessageBox,
  ConvertArray,
  FullConvertUrlArray,
  GetFileNameByPath,
  GetFileTypeByPath,
  ReplaceImageHttp,
  YMDHMSFormat,
  YMDFormat,
  HMSFormat,
  GetRelativeTimeDesc,
  GetVideoDuration,
  GetAudioDuration,
};
